<template>
  <div class="company-search-select">
    <VueSelect
      v-model="selected"
      :options="options"
      :filterable="false"
      :reduce="(option: any) => option.handle"
      :placeholder="placeholder"
      :loading="loading"
      label="label"
      @search="onSearch"
    >
      <template #option="option">
        <div class="company-option">
          <span class="company-option__name">{{ option.label }}</span>
          <span v-if="option.clientNumber" class="company-option__meta"> ({{ option.clientNumber }})</span>
        </div>
      </template>
      <template #no-options="{ search }">
        <span v-if="search.length < 2">Type to search...</span>
        <span v-else>No companies found</span>
      </template>
    </VueSelect>
  </div>
</template>

<script lang="ts">
import type { Company } from '@/types/Models/Company'
import { defineComponent, ref, watch, onMounted, type PropType } from 'vue'
import { useCustomersStore } from '@/store/customers'
import VueSelect from 'vue-select'

export default defineComponent({
  name: 'CompanySearchSelect',
  components: { VueSelect },
  props: {
    modelValue: {
      type: String as PropType<string | null>,
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Search company...',
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const customersStore = useCustomersStore()
    const options = ref<Array<{ handle: string; label: string; clientNumber: string }>>([])
    const loading = ref(false)
    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    const selected = ref<string | null>(props.modelValue)

    const mapCompanyToOption = (company: Company) => ({
      handle: company.id,
      label: company.name || company.contactName || company.email,
      clientNumber: company.clientNumber || '',
    })

    const searchCompanies = (query: string) => {
      loading.value = true
      customersStore
        .fetchAll({ query, limit: 20 })
        .then(() => {
          options.value = customersStore.allCustomers.map((c) => ({
            handle: c.handle,
            label: c.label,
            clientNumber: (c as Company).clientNumber || '',
          }))
        })
        .finally(() => {
          loading.value = false
        })
    }

    const onSearch = (query: string) => {
      if (debounceTimer) clearTimeout(debounceTimer)
      if (query.length < 2) {
        options.value = []
        return
      }
      debounceTimer = setTimeout(() => searchCompanies(query), 300)
    }

    const loadInitialValue = (id: string) => {
      loading.value = true
      customersStore
        .fetch(id)
        .then(() => {
          if (customersStore.customer) {
            options.value = [mapCompanyToOption(customersStore.customer)]
          }
        })
        .finally(() => {
          loading.value = false
        })
    }

    watch(selected, (newVal) => {
      emit('update:modelValue', newVal || null)
    })

    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal !== selected.value) {
          selected.value = newVal
          if (newVal && !options.value.find((o) => o.handle === newVal)) {
            loadInitialValue(newVal)
          }
        }
      },
    )

    onMounted(() => {
      if (props.modelValue) {
        loadInitialValue(props.modelValue)
      }
    })

    return {
      selected,
      options,
      loading,
      onSearch,
    }
  },
})
</script>

